// SPDX-FileCopyrightText: Copyright 2026 Dash0 Inc.
"use client";

import { useEffect } from "react";

declare global {
    interface Navigator {
        modelContext?: {
            registerTool(
                tool: {
                    name: string;
                    title?: string;
                    description: string;
                    inputSchema?: object;
                    execute: (input: Record<string, unknown>) => Promise<unknown>;
                    annotations?: { readOnlyHint?: boolean };
                },
                options?: { signal?: AbortSignal },
            ): void;
        };
    }
}

export function WebMCPTools() {
    useEffect(() => {
        if (!navigator.modelContext) return;

        const controller = new AbortController();
        const { signal } = controller;
        const mc = navigator.modelContext;

        try {
            mc.registerTool(
                {
                    name: "search",
                    title: "Search Dash0",
                    description:
                        "Search the Dash0 website for documentation, blog posts, integrations, and product information. Returns a URL to the search results page.",
                    inputSchema: {
                        type: "object",
                        properties: {
                            query: { type: "string", description: "The search query" },
                        },
                        required: ["query"],
                    },
                    annotations: { readOnlyHint: true },
                    execute: async ({ query }) => ({
                        url: `https://www.dash0.com/search?q=${encodeURIComponent(String(query))}`,
                    }),
                },
                { signal },
            );

            mc.registerTool(
                {
                    name: "get_pricing",
                    title: "Get Dash0 Pricing",
                    description:
                        "Returns the Dash0 pricing page URL. Dash0 uses usage-based pricing with a free tier.",
                    annotations: { readOnlyHint: true },
                    execute: async () => ({
                        url: "https://www.dash0.com/pricing",
                    }),
                },
                { signal },
            );

            mc.registerTool(
                {
                    name: "book_demo",
                    title: "Book a Dash0 Demo",
                    description:
                        "Returns the URL to book a live demo of Dash0 with the team.",
                    annotations: { readOnlyHint: true },
                    execute: async () => ({
                        url: "https://www.dash0.com/book-a-demo",
                    }),
                },
                { signal },
            );

            mc.registerTool(
                {
                    name: "get_documentation",
                    title: "Get Dash0 Documentation",
                    description:
                        "Returns the Dash0 documentation base URL and the entry pages of its main sections: getting started, AI, telemetry signals, querying, dashboards, monitoring, alerting, cost control, and the glossary.",
                    annotations: { readOnlyHint: true },
                    execute: async () => ({
                        baseUrl: "https://www.dash0.com/docs/dash0",
                        sections: [
                            { name: "Get Started", url: "https://www.dash0.com/docs/dash0/get-started" },
                            { name: "Use AI with Dash0", url: "https://www.dash0.com/docs/dash0/get-started/use-ai" },
                            { name: "Agent0", url: "https://www.dash0.com/docs/dash0/ai/agent0/overview" },
                            { name: "Darkplane", url: "https://www.dash0.com/docs/dash0/darkplane/overview" },
                            { name: "Logs", url: "https://www.dash0.com/docs/dash0/telemetry/logging/about-logging" },
                            { name: "Traces", url: "https://www.dash0.com/docs/dash0/telemetry/tracing/about-tracing" },
                            { name: "Metrics", url: "https://www.dash0.com/docs/dash0/telemetry/metrics/about-metrics" },
                            { name: "Web Events", url: "https://www.dash0.com/docs/dash0/telemetry/web-events/about-web-events" },
                            { name: "Query Data", url: "https://www.dash0.com/docs/dash0/telemetry/query-data/query-builder/about-query-builder" },
                            { name: "Dashboards", url: "https://www.dash0.com/docs/dash0/dashboards/about-dashboards" },
                            { name: "Services", url: "https://www.dash0.com/docs/dash0/monitoring/services/about-services" },
                            { name: "Resources", url: "https://www.dash0.com/docs/dash0/monitoring/resources/about-resources" },
                            { name: "Kubernetes", url: "https://www.dash0.com/docs/dash0/monitoring/kubernetes/about-kubernetes" },
                            { name: "Alerting", url: "https://www.dash0.com/docs/dash0/monitoring/alerting/alerting" },
                            { name: "Website Monitoring", url: "https://www.dash0.com/docs/dash0/monitoring/websites/website-monitoring" },
                            { name: "Synthetic Monitoring", url: "https://www.dash0.com/docs/dash0/monitoring/synthetics/synthetic-monitoring" },
                            { name: "Billing & Cost Control", url: "https://www.dash0.com/docs/dash0/cost-control/overview" },
                            { name: "Glossary", url: "https://www.dash0.com/docs/dash0/miscellaneous/glossary/overview" },
                        ],
                    }),
                },
                { signal },
            );

            mc.registerTool(
                {
                    name: "get_api_docs",
                    title: "Get Dash0 API Documentation",
                    description:
                        "Returns the Dash0 API reference documentation URL. Covers endpoints for ingesting OTLP logs and spans, managing alerting rules, check rules, and other platform operations. Also returns the OAuth 2.0 discovery endpoint for programmatic authentication.",
                    annotations: { readOnlyHint: true },
                    execute: async () => ({
                        url: "https://www.dash0.com/docs/api-reference",
                        oauthDiscovery: "https://api.eu-west-1.aws.dash0.com/.well-known/oauth-authorization-server",
                    }),
                },
                { signal },
            );

            mc.registerTool(
                {
                    name: "get_mcp_connection_info",
                    title: "Get Dash0 MCP Server Info",
                    description:
                        "Returns information on how to connect an AI assistant to Dash0 via the Model Context Protocol (MCP), including the transport type, endpoint pattern, and authentication method.",
                    annotations: { readOnlyHint: true },
                    execute: async () => ({
                        transport: "streamable-http",
                        endpointPattern: "https://api.{region}.aws.dash0.com/mcp",
                        authentication: "Bearer token via Authorization header",
                        instructions:
                            "Log in to the Dash0 app, go to Settings → Endpoints → MCP to copy your org-specific endpoint URL, then create an auth token under Settings → Auth Tokens.",
                        setupGuide: "https://www.dash0.com/docs/dash0/get-started/use-ai",
                        integrationHub: "https://www.dash0.com/hub/integrations?tag=AI",
                    }),
                },
                { signal },
            );
        } catch (e) {
            console.warn("WebMCP tool registration failed:", e);
        }

        return () => controller.abort();
    }, []);

    return null;
}
